"use client";

import { useState } from "react";
import { Button } from "@tillix/ui-atoms/button";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@tillix/ui-atoms/input-group";
import { formatAccountNumber } from "@tillix/utils";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";

export interface AccountNumberInputProps {
  name?: string;
  id: string;
}

export function AccountNumberInput({
  name = "accountNumber",
  id,
}: AccountNumberInputProps) {
  const { control } = useFormContext();
  const { t } = useT();
  const { t: tInputs } = useT("inputs");
  const [revealed, setRevealed] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldRoot name={field.name}>
          <FieldLabel htmlFor={id}>{t("paymentForm.accountNumber")}</FieldLabel>
          <FieldControl
            render={(defaultProps) => (
              <InputGroup>
                <InputGroupInput
                  {...defaultProps}
                  autoComplete="off"
                  data-1p-ignore=""
                  data-lpignore="true"
                  id={id}
                  inputMode="numeric"
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={(e) =>
                    field.onChange(formatAccountNumber(e.target.value))
                  }
                  placeholder={t("paymentForm.accountNumber")}
                  ref={field.ref}
                  type={revealed ? "text" : "password"}
                  value={field.value as string}
                />
                <InputGroupAddon align="inline-end">
                  <Button
                    aria-controls={id}
                    aria-label={
                      revealed
                        ? t("paymentForm.hideAccountNumber")
                        : t("paymentForm.showAccountNumber")
                    }
                    aria-pressed={revealed}
                    onClick={() => setRevealed((r) => !r)}
                    type="button"
                    variant="ghost"
                  >
                    {revealed ? (
                      <EyeOffIcon aria-hidden="true" size={16} />
                    ) : (
                      <EyeIcon aria-hidden="true" size={16} />
                    )}
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <FieldError>
            {fieldState.error
              ? tInputs("validation.invalidAccountNumber")
              : undefined}
          </FieldError>
        </FieldRoot>
      )}
    />
  );
}
